// Two-pane input (paste / drop / open a file). ONE input, NO format tabs (spec §8.1):
// format is auto-detected per pane and surfaced as a click-to-override pill.
// Minimal-by-default toolbar (spec §8.3): detection pill · key column (record
// formats only) · Options · Format · Compare — everything else lives behind the
// Options disclosure. Sensible defaults are pre-applied (ignore whitespace ON,
// ignore key order ON for JSON/YAML).
// No standing format/limit help under each pane: it duplicated itself across both
// panes for a rule that is global, and every rejection already carries UPLOAD_HELP
// (upload-formats.js) or the byte-limit message (input-limits.js). The budget is
// checked BEFORE any read, so an over-limit selection fails instantly and for free
// — teaching at failure costs the reader nothing here. The one fact with no other
// home on this page, Parquet folders/parts, is stated on the buttons themselves.
import { UPLOAD_ACCEPT, UPLOAD_FORMATS_HELP } from '../lib/upload-formats.js';
import { FILE_ICON, FOLDER_ICON } from './icons.js';

const DISPLAY = { text: 'Text', json: 'JSON', yaml: 'YAML', csv: 'CSV', tsv: 'TSV' };
const OVERRIDE = ['text', 'json', 'yaml', 'csv', 'tsv']; // V1 engines only (XML is V2)

export function tool() {
  return `
  <section class="tool" id="inputCard" aria-labelledby="inputHeading">
    <h2 class="sr-only" id="inputHeading">Compare inputs</h2>
    <div class="panes">
      ${pane('left', 'Original', 'leftTa', 'leftFile', 'leftFileInput', 'Paste anything — text, code, JSON, YAML, CSV. cruxdiff figures out the rest.')}
      ${pane('right', 'Changed', 'rightTa', 'rightFile', 'rightFileInput', 'Paste the version to compare against.')}
    </div>
    <div class="tool-foot">
      <span class="detected" id="detected" role="button" tabindex="0" aria-haspopup="menu" aria-expanded="false" title="Detected automatically — click to override">
        <span class="dpulse"></span><span id="detText">detecting…</span> <span class="dcaret">&#9662;</span>
      </span>
      <div class="pop det-menu" id="detMenu" role="menu" aria-label="Override detected format">
        <button class="det-opt" role="menuitem" data-fmt="auto">Auto-detect</button>
        ${OVERRIDE.map((f) => `<button class="det-opt" role="menuitem" data-fmt="${f}">${DISPLAY[f]}</button>`).join('')}
        <!-- Parquet is listed so the menu states the full set of supported formats, but it
             is never a choice you can apply to pasted text: the engine needs a decoded
             document, not a string. main.js enables it only while a Parquet file is open
             (where it reports the current format); otherwise aria-disabled + the hint says
             how to get there. aria-disabled, not [disabled], keeps it announced and
             reachable by arrow keys. -->
        <button class="det-opt" role="menuitem" data-fmt="parquet" aria-disabled="true">Parquet<span class="det-hint"> — open a file or folder</span></button>
      </div>
      <span class="keycol" id="keycolWrap" style="display:none">matched by
        <select aria-label="Key column"></select>
      </span>
      <button class="ghost-btn" id="optBtn" aria-haspopup="dialog" aria-expanded="false">Options</button>
      <div class="pop opt-pop" id="optPop" role="dialog" aria-label="Comparison options">
        <h5>Options <span class="opt-sub">— sensible defaults already applied</span></h5>
        <label><input type="checkbox" id="optWhitespace" checked><span><b>Ignore whitespace</b></span></label>
        <label><input type="checkbox" id="optCase"><span><b>Ignore case</b></span></label>
        <label><input type="checkbox" id="optKeyOrder" checked><span><b>Ignore key order</b><div class="note">JSON/YAML only — reordering isn't a change</div></span></label>
        <label class="opt-block"><b>Ignore fields</b><div class="note">comma-separated — e.g. updated_at, nonce</div>
          <input type="text" class="opt-input" id="ignoreFields" aria-label="Ignore fields (comma separated)" placeholder="updated_at, nonce" spellcheck="false"></label>
        <button class="cta" id="optDone">Done</button>
      </div>
      <button class="ghost-btn" id="fmtBtn" title="Prettify minified input before diffing">&#8984; Format</button>
      <button class="ghost-btn" id="sampleBtn" title="Load a sample comparison to see move detection">&#9889; Try with sample</button>
      <button class="compare-btn" id="compareBtn">Compare &rarr;</button>
    </div>
    <p class="size-note" id="sizeNote" role="status" hidden></p>
    <p class="upload-error" id="comparisonError" role="alert" hidden></p>
  </section>`;
}

function pane(side, label, taId, fileId, inputId, placeholder) {
  return `
  <div class="pane" data-pane="${side}">
    <div class="pane-label">
      <span>${label}</span>
      <span class="pane-tools">
        <span class="fname" id="${fileId}"></span>
        <button class="clear-file-btn" type="button" data-clear="${side}" aria-label="Clear ${label} file" hidden>Clear file</button>
        <button class="upload-btn" data-input="${inputId}" aria-label="Open files in the ${label} pane" title="${UPLOAD_FORMATS_HELP}">${FILE_ICON}Open files</button>
        <button class="folder-btn upload-btn" data-input="${side}FolderInput" aria-label="Open a Parquet folder in the ${label} pane" title="Combine a folder of .parquet parts into one table — partition folders such as year=2026 become columns">${FOLDER_ICON}Open <span class="btn-long">parquet </span>folder</button>
      </span>
    </div>
    <input type="file" id="${inputId}" class="file-input" multiple accept="${UPLOAD_ACCEPT}" aria-label="Open files in the ${label} pane" hidden>
    <input type="file" id="${side}FolderInput" webkitdirectory multiple aria-label="Open a Parquet folder in the ${label} pane" hidden>
    <textarea id="${taId}" aria-label="${label} content" spellcheck="false" placeholder="${placeholder}"></textarea>
    <div class="file-viewer" id="${side}FileViewer" aria-label="${label} file viewer" hidden></div>
    <p class="upload-error loading" id="${side}UploadError" role="status" hidden></p>
    <div class="drop-hint" aria-hidden="true">drop file to load</div>
  </div>`;
}
